import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from "react";
import { useLocation } from "react-router-dom";
import { message, notification } from "antd";
import { AuthContext } from "../context/AuthProvider";
import { getApiUrl, getExpressApiBaseUrl } from "../lib/api/config";
import { getAuth } from "firebase/auth";
import {
  readUrlHadNonemptySecret,
  consumeOpsSecretFromUrl,
} from "../lib/opsUrlSecret";
import {
  consumeAdminAuthTokenFromUrl,
} from "../lib/adminUrlSecret";
import AdminPerformancePanel from "../components/admin/AdminPerformancePanel";

const REFRESH_INTERVAL = 10000; // 10 seconds for surgical precision

/** Minimal 404 - no route metadata for unauthorized /admin hits */
const AdminBlank404 = () => (
  <div
    className="min-h-screen bg-white flex items-center justify-center"
    aria-hidden="true"
  >
    <div className="font-mono text-6xl font-bold text-slate-200 select-none">
      404
    </div>
  </div>
);

/** Shown when Twilio / Instantly health reports DOWN and dispatch auto-pauses */
const MESSAGING_OUTAGE_FIX_STEPS = [
  "Health is driven by recent rows in Firestore collection apiErrors (and in-memory logs), not by the Netlify env UI. Stale errors or a failed health query used to mark Twilio DOWN incorrectly; deploy the latest API and clear old apiErrors if the list still looks wrong.",
  "Twilio: confirm TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, and TWILIO_PHONE_NUMBER on the API runtime (not only the SPA site); verify the Twilio Console for suspension or auth failures.",
  "Instantly: optional. With no INSTANTLY_API_KEY the API now reports Instantly as healthy. If you use Instantly, set INSTANTLY_API_KEY and redeploy.",
  "Redeploy the API, refresh Mission Control, open GET /ops-dashboard/api-health, then unpause dispatch when Twilio shows healthy.",
];

const ShadowAuditsMap = lazy(() => import("../components/ops/ShadowAuditsMap"));

// Status Ring - numeric `value` = % arc; optional `centerLabel` overrides center text (e.g. hunt count, LIVE).
const StatusRing = ({
  status,
  label,
  value,
  centerLabel,
  size = "md",
  className = "",
}) => {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16",
    xl: "w-20 h-20",
  };

  const statusColors = {
    operational: "stroke-surgical-teal shadow-surgical",
    warning: "stroke-surgical-orange shadow-surgical-alert",
    critical: "stroke-red-500 shadow-surgical-critical",
    offline: "stroke-gray-500",
  };

  const ringPct =
    typeof value === "number" ? Math.max(0, Math.min(100, value)) : 100;
  const circumference = 2 * Math.PI * 16; // radius = 16
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (ringPct / 100) * circumference;
  const centerText =
    centerLabel != null && centerLabel !== ""
      ? centerLabel
      : typeof value === "number"
      ? `${Math.round(ringPct)}%`
      : null;
  const pulseRing =
    status === "operational" || status === "warning" || status === "critical";

  return (
    <div className={`flex flex-col items-center space-y-2 ${className}`}>
      <div className={`relative ${sizeClasses[size]}`}>
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
          {/* Background circle */}
          <circle
            cx="18"
            cy="18"
            r="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-surgical-navy-600 opacity-30"
          />
          {/* Progress circle */}
          <circle
            cx="18"
            cy="18"
            r="16"
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            className={`${
              statusColors[status] || statusColors.offline
            } transition-all duration-500 ${
              pulseRing ? "animate-pulse-surgical" : ""
            }`}
            style={{
              transition: "stroke-dashoffset 0.5s ease-in-out",
            }}
          />
        </svg>
        {centerText != null && (
          <div className="absolute inset-0 flex items-center justify-center px-0.5">
            <span
              className={`font-surgical font-bold text-white text-center leading-none ${
                size === "sm" ? "text-[9px]" : "text-xs"
              }`}
            >
              {centerText}
            </span>
          </div>
        )}
      </div>
      {label ? (
        <div className="text-center">
          <div className="font-surgical text-xs font-bold text-surgical-teal uppercase tracking-wider">
            {label}
          </div>
        </div>
      ) : null}
    </div>
  );
};

/** Expandable per-variable env audit (keys only; presence booleans from API). */
function PreflightEnvInventory({ report }) {
  if (!report?.categories?.length) {
    return (
      <p className="mt-6 font-surgical text-xs text-gray-500 border-t border-surgical-navy-600 pt-4">
        Preflight environment inventory is not available from this API build.
        Deploy the latest backend and refresh.
      </p>
    );
  }

  const generated =
    report.generatedAt && !Number.isNaN(new Date(report.generatedAt).getTime())
      ? new Date(report.generatedAt).toLocaleString()
      : " - ";

  return (
    <details className="group mt-6 rounded-surgical border border-surgical-navy-600 bg-surgical-navy-900/70 open:border-surgical-orange/40 [&_summary::-webkit-details-marker]:hidden">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 font-surgical text-xs font-bold uppercase tracking-wider text-surgical-orange hover:bg-surgical-navy-800/90">
        <span className="select-none">
          Environment inventory - click to expand
        </span>
        <span className="font-surgical text-[10px] font-normal normal-case text-gray-400">
          NODE_ENV={report.nodeEnv || "?"}
          {report.deployment ? ` · ${report.deployment}` : ""} · {generated}
        </span>
      </summary>
      <div className="border-t border-surgical-navy-600 px-4 py-4 space-y-6 max-h-[min(70vh,520px)] overflow-y-auto">
        {report.categories.map((cat) => (
          <section key={cat.id} className="space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="font-surgical text-sm font-bold text-surgical-teal uppercase tracking-wide">
                {cat.title}
              </h3>
              <span
                className={`font-surgical text-xs font-bold uppercase ${
                  cat.status === "operational"
                    ? "text-surgical-teal"
                    : cat.status === "warning"
                    ? "text-surgical-orange"
                    : "text-red-400"
                }`}
              >
                {cat.scorePercent}% · {cat.status}
              </span>
            </div>
            <p className="font-surgical text-xs text-gray-400 leading-relaxed">
              {cat.description}
            </p>
            {cat.missingRequired?.length ? (
              <div className="font-surgical text-xs text-surgical-orange bg-surgical-navy-800/80 border border-surgical-orange/30 rounded px-3 py-2">
                <span className="font-bold uppercase">
                  Missing / unresolved:{" "}
                </span>
                {cat.missingRequired.join(", ")}
              </div>
            ) : null}
            <ul className="space-y-1.5 border border-surgical-navy-700 rounded p-3 bg-surgical-navy-900/60">
              {cat.checks.map((row) => (
                <li
                  key={row.key}
                  className="flex flex-wrap items-start justify-between gap-2 font-surgical text-xs"
                >
                  <span className="text-gray-300 min-w-0 flex-1">
                    <span
                      className={
                        row.present
                          ? "text-surgical-teal"
                          : "text-surgical-orange"
                      }
                    >
                      {row.present ? "✓" : "✗"}
                    </span>{" "}
                    <code className="text-surgical-teal/90 bg-surgical-navy-800 px-1 rounded text-[11px]">
                      {row.key}
                    </code>
                    {row.optional ? (
                      <span className="text-gray-500 normal-case">
                        {" "}
                        (optional)
                      </span>
                    ) : null}
                    <span className="text-gray-400 normal-case block sm:inline sm:ml-1">
                      {" "}
                      - {row.label}
                    </span>
                  </span>
                  <span
                    className={`shrink-0 text-right font-surgical text-[11px] font-bold uppercase tabular-nums ${
                      row.present ? "text-surgical-teal" : "text-red-400"
                    }`}
                  >
                    {row.present ? "set" : "missing"}
                    {typeof row.valueLength === "number" ? (
                      <span className="block font-normal normal-case text-gray-500 mt-0.5">
                        raw {row.valueLength} chars · trimmed{" "}
                        {row.trimmedLength ?? 0} · {row.lineCount ?? 0} line
                        {(row.lineCount ?? 0) === 1 ? "" : "s"}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <p className="font-surgical text-[10px] text-gray-500 leading-relaxed pb-1">
          Secret values are never sent - only presence plus raw / trimmed
          character counts and line counts from the API host. Add missing keys
          in Netlify (or your host) and redeploy.
        </p>
      </div>
    </details>
  );
}

// Sparkline Component for Lead Velocity
const LeadVelocitySparkline = ({ data = [] }) => {
  if (!data.length) return null;

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  const denom = Math.max(1, data.length - 1);
  const points = data
    .map((value, index) => {
      const x = (index / denom) * 200; // 200px width
      const y = 40 - ((value - min) / range) * 30; // 30px height, inverted
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="relative w-full h-12 bg-surgical-navy-800 rounded border border-surgical-navy-600">
      <svg className="w-full h-full" viewBox="0 0 200 40">
        <polyline
          fill="none"
          stroke="#14B8A6"
          strokeWidth="2"
          points={points}
          className="drop-shadow-sm"
        />
        {/* Gradient fill */}
        <defs>
          <linearGradient
            id="velocityGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop
              offset="0%"
              style={{ stopColor: "#14B8A6", stopOpacity: 0.3 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: "#14B8A6", stopOpacity: 0 }}
            />
          </linearGradient>
        </defs>
        <polygon
          fill="url(#velocityGradient)"
          points={`0,40 ${points} 200,40`}
        />
      </svg>
      <div className="absolute top-1 right-1 font-surgical text-xs text-surgical-teal">
        {data[data.length - 1] || 0}/min
      </div>
    </div>
  );
};

// Ops Alert Overlay Component
const OpsAlertOverlay = ({ isVisible, alerts, onClose, onPauseDispatch }) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
      <div className="bg-surgical-navy-900 border-2 border-surgical-orange rounded-surgical p-8 max-w-2xl mx-4 shadow-surgical-critical">
        <div className="flex items-center space-x-4 mb-6">
          <div className="h-3 w-3 shrink-0 rounded-full bg-surgical-orange shadow-[0_0_12px_rgba(245,158,11,0.75)]" />
          <h2 className="font-surgical text-2xl font-bold text-surgical-orange uppercase tracking-wider">
            OPERATIONAL ALERT
          </h2>
        </div>

        <div className="space-y-4 mb-8">
          {alerts.map((alert, index) => (
            <div
              key={alert.id || `alert-${index}`}
              className="bg-surgical-navy-800 border border-surgical-orange rounded p-4"
            >
              <div className="font-surgical text-sm font-bold text-surgical-orange mb-2">
                {alert.service} - {alert.status}
              </div>
              <div className="font-surgical text-xs text-gray-300">
                {alert.message}
              </div>
              {Array.isArray(alert.remediationSteps) &&
              alert.remediationSteps.length > 0 ? (
                <div className="mt-3 border-t border-surgical-navy-600 pt-3">
                  <div className="font-surgical text-[10px] font-bold uppercase tracking-wider text-surgical-orange mb-2">
                    What to try
                  </div>
                  <ul className="m-0 list-disc space-y-1.5 pl-4 font-surgical text-[11px] leading-relaxed text-gray-400">
                    {alert.remediationSteps.map((step, si) => (
                      <li key={si}>{step}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="font-surgical text-xs text-gray-500 mt-2">
                {new Date(alert.timestamp).toLocaleTimeString()}
              </div>
            </div>
          ))}
        </div>

        <div className="flex space-x-4">
          <button
            onClick={onPauseDispatch}
            className="flex-1 bg-surgical-orange hover:bg-surgical-orange-600 text-surgical-navy-900 font-surgical font-bold py-3 px-6 rounded transition-colors"
          >
            PAUSE DISPATCH ENGINE
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-surgical-navy-700 hover:bg-surgical-navy-600 text-white font-surgical font-bold py-3 px-6 rounded transition-colors"
          >
            ACKNOWLEDGE
          </button>
        </div>
      </div>
    </div>
  );
};

const MissionControlSurgical = () => {
  const location = useLocation();
  const isUnifiedAdminRoute = location.pathname === "/admin";
  const { user, userInfo } = useContext(AuthContext);
  const [urlHadSecretOnMount] = useState(() => readUrlHadNonemptySecret());
  const [adminAuthToken] = useState(() => consumeAdminAuthTokenFromUrl());
  const [opsAppSecret] = useState(() => consumeOpsSecretFromUrl());
  const appSecret = adminAuthToken || opsAppSecret;
  const [authRejected, setAuthRejected] = useState(false);
  const [authClaims, setAuthClaims] = useState({ admin: false, pending: true });
  const [data, setData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [activeChatSessions, setActiveChatSessions] = useState([]);
  const [leadVelocityData, setLeadVelocityData] = useState([]);
  const [opsAlerts, setOpsAlerts] = useState([]);
  const [showOpsAlert, setShowOpsAlert] = useState(false);
  const [dispatchPaused, setDispatchPaused] = useState(false);
  const [surgicalMetrics, setSurgicalMetrics] = useState(null);
  const [adminMetrics, setAdminMetrics] = useState(null);
  const [adminMetricsLoading, setAdminMetricsLoading] = useState(false);
  const [apiHealthData, setApiHealthData] = useState([]);
  /** Fire at most once per session when Twilio/Instantly report DOWN */
  const autoPausedForMessagingRef = useRef(false);

  const apiUrl = getApiUrl();

  // Auth claims effect
  useEffect(() => {
    if (!user?.uid) {
      setAuthClaims({ admin: false, pending: false });
      return;
    }
    let cancelled = false;
    setAuthClaims((s) => ({ ...s, pending: true }));
    const cu = getAuth().currentUser;
    if (!cu) {
      setAuthClaims({ admin: false, pending: false });
      return;
    }
    cu.getIdTokenResult()
      .then((r) => {
        if (!cancelled) {
          setAuthClaims({
            admin: r?.claims?.admin === true,
            pending: false,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setAuthClaims({ admin: false, pending: false });
      });
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  const isAdmin =
    authClaims.admin === true || userInfo?.customClaims?.admin === true;
  /** /admin: session or URL auth_token; legacy /ops: ?secret= on first paint or Firebase admin */
  const isUiUnlocked = isUnifiedAdminRoute
    ? Boolean(appSecret) || isAdmin
    : urlHadSecretOnMount || isAdmin;
  const isAuthorized = isUiUnlocked && (Boolean(appSecret) || isAdmin);

  const buildOpsAuthHeaders = useCallback(async () => {
    const headers = { "Content-Type": "application/json" };
    if (appSecret) {
      headers["X-App-Secret"] = appSecret;
      return headers;
    }
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("No Firebase session");
    }
    headers["Authorization"] = `Bearer ${token}`;
    return headers;
  }, [appSecret]);

  const rejectAdminAuth = useCallback(() => {
    if (isUnifiedAdminRoute) setAuthRejected(true);
  }, [isUnifiedAdminRoute]);

  const fetchAdminMetrics = useCallback(async () => {
    if (!appSecret) return;
    setAdminMetricsLoading(true);
    try {
      const base = getExpressApiBaseUrl();
      const headers = { "X-App-Secret": appSecret };
      const response = await fetch(
        `${base}/admin/metrics?auth_token=${encodeURIComponent(appSecret)}`,
        { headers, credentials: "omit" }
      );
      if (response.status === 404 || response.status === 401) {
        rejectAdminAuth();
        return;
      }
      if (!response.ok) return;
      const body = await response.json();
      if (body?.ok && body.metrics) {
        setAdminMetrics(body.metrics);
      } else {
        rejectAdminAuth();
      }
    } catch {
      /* non-fatal for legacy /ops */
    } finally {
      setAdminMetricsLoading(false);
    }
  }, [appSecret, rejectAdminAuth]);

  const fetchHealthStatus = async () => {
    try {
      const headers = await buildOpsAuthHeaders();
      const [healthResponse, apiHealthResponse] = await Promise.all([
        fetch(`${apiUrl}/ops-dashboard/health`, { headers }),
        fetch(`${apiUrl}/ops-dashboard/api-health`, { headers }),
      ]);

      if (healthResponse.ok) {
        const result = await healthResponse.json();
        setHealthData(result);
      }

      if (apiHealthResponse.ok) {
        const apiResult = await apiHealthResponse.json();
        const services = apiResult.services || [];
        setApiHealthData(services);

        const messagingDown = services.filter(
          (s) =>
            s.status === "down" &&
            ["twilio", "instantly"].includes(
              String(s.service || "").toLowerCase()
            )
        );
        if (messagingDown.length > 0 && !autoPausedForMessagingRef.current) {
          autoPausedForMessagingRef.current = true;
          const ts = new Date().toISOString();
          setOpsAlerts((prev) => [
            {
              id: `auto-api-${Date.now()}`,
              service: messagingDown.map((s) => s.service).join(" + "),
              status: "DOWN",
              message:
                "Twilio or Instantly health check reported DOWN. Dispatch engine auto-paused.",
              remediationSteps: [...MESSAGING_OUTAGE_FIX_STEPS],
              timestamp: ts,
            },
            ...prev.slice(0, 8),
          ]);
          setShowOpsAlert(true);
          try {
            const h = await buildOpsAuthHeaders();
            const pr = await fetch(`${apiUrl}/ops-dashboard/pause-dispatch`, {
              method: "POST",
              headers: h,
              body: JSON.stringify({
                reason:
                  "Automatic pause: Twilio/Instantly API DOWN (Mission Control health poll)",
              }),
            });
            if (pr.ok) {
              setDispatchPaused(true);
              notification.warning({
                message: "Dispatch auto-paused (messaging outage)",
                description: (
                  <div className="text-left text-[13px] font-normal font-sans leading-snug text-gray-700">
                    <p className="mb-2 mt-0.5 text-gray-800">
                      Outbound messaging health reported Twilio or Instantly as
                      DOWN.
                    </p>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-900">
                      What to try
                    </p>
                    <ul className="m-0 list-disc space-y-1.5 pl-4">
                      {MESSAGING_OUTAGE_FIX_STEPS.map((text, i) => (
                        <li key={i}>{text}</li>
                      ))}
                    </ul>
                  </div>
                ),
                placement: "top",
                duration: 16,
                style: { width: 440, maxWidth: "calc(100vw - 24px)" },
              });
            }
          } catch (pauseErr) {
            console.error("[MissionControl] auto-pause failed", pauseErr);
          }
        }
      }

      // Generate mock lead velocity data
      setLeadVelocityData((prev) => {
        const newData = [...prev, Math.floor(Math.random() * 20) + 10];
        return newData.slice(-20); // Keep last 20 points
      });
    } catch (e) {
      console.error("Health check failed:", e);
    }
  };

  const fetchOpsAlerts = async () => {
    try {
      const headers = await buildOpsAuthHeaders();
      const response = await fetch(
        `${apiUrl}/ops-dashboard/ops-alerts?limit=5`,
        { headers }
      );

      if (response.ok) {
        const result = await response.json();
        setOpsAlerts(result.alerts || []);
        setDispatchPaused(result.dispatchPaused || false);

        // Show alert overlay if there are unacknowledged alerts
        const hasUnacknowledged = result.alerts?.some(
          (alert) => !alert.acknowledged
        );
        setShowOpsAlert(hasUnacknowledged);
      }
    } catch (e) {
      console.error("Failed to fetch ops alerts:", e);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const headers = await buildOpsAuthHeaders();
      const response = await fetch(`${apiUrl}/ops-dashboard/enhanced-data`, {
        headers,
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => "");
        console.error("[MissionControl] enhanced-data failed", {
          status: response.status,
          url: response.url,
          body: errText?.slice(0, 500),
        });

        if (
          isUnifiedAdminRoute &&
          (response.status === 401 ||
            response.status === 403 ||
            response.status === 404)
        ) {
          rejectAdminAuth();
          return;
        }

        // Simulate ops alert for 4xx/5xx errors
        if (response.status >= 400) {
          setOpsAlerts((prev) => [
            {
              id: Date.now(),
              service: "Mission Control API",
              status: `HTTP ${response.status}`,
              message: `API endpoint returned ${response.status}: ${response.statusText}`,
              timestamp: new Date().toISOString(),
            },
            ...prev.slice(0, 4),
          ]);
          setShowOpsAlert(true);
        }

        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      if (!result.ok) {
        throw new Error(result.error || "Unknown error");
      }

      setData(result);
      setActiveChatSessions(result.activeChatSessions || []);
      setSurgicalMetrics(result.surgicalMetrics || null);
      setError(null);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionDetails = async (sessionId) => {
    try {
      const headers = await buildOpsAuthHeaders();
      const response = await fetch(
        `${apiUrl}/ops-dashboard/session/${sessionId}`,
        {
          headers,
        }
      );

      if (response.ok) {
        const sessionData = await response.json();
        setSelectedSession(sessionData);
      }
    } catch (e) {
      message.error("Failed to load session details");
    }
  };

  const triggerManualIntervention = async (jobId, action) => {
    try {
      const headers = await buildOpsAuthHeaders();
      const response = await fetch(
        `${apiUrl}/ops-dashboard/trigger-followup/${jobId}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ action }),
        }
      );

      const result = await response.json();
      if (result.ok) {
        message.success(`Manual intervention triggered for job ${jobId}`);
        fetchDashboardData();
      } else {
        message.error(`Error: ${result.error}`);
      }
    } catch (e) {
      message.error(`Error triggering intervention: ${e.message}`);
    }
  };

  const handlePauseDispatch = async () => {
    try {
      const headers = await buildOpsAuthHeaders();
      const response = await fetch(`${apiUrl}/ops-dashboard/pause-dispatch`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          reason: "Manual pause due to operational alerts",
        }),
      });

      if (response.ok) {
        setDispatchPaused(true);
        setShowOpsAlert(false);
        message.warning(
          "Dispatch engine paused. Manual intervention required."
        );
      } else {
        message.error("Failed to pause dispatch engine");
      }
    } catch (e) {
      console.error("Error pausing dispatch:", e);
      message.error("Error pausing dispatch engine");
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      const headers = await buildOpsAuthHeaders();
      await fetch(`${apiUrl}/ops-dashboard/acknowledge-alert/${alertId}`, {
        method: "POST",
        headers,
      });

      // Refresh alerts after acknowledgment
      fetchOpsAlerts();
    } catch (e) {
      console.error("Error acknowledging alert:", e);
    }
  };

  // Initial load
  useEffect(() => {
    if (!isAuthorized) return;
    if (!appSecret && user?.uid && authClaims.pending) return;
    fetchHealthStatus();
    fetchDashboardData();
    fetchOpsAlerts();
    fetchAdminMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, isAuthorized, appSecret, authClaims.pending]);

  // Auto-refresh
  useEffect(() => {
    if (!data || !isAuthorized) return;

    const interval = setInterval(() => {
      fetchHealthStatus();
      fetchDashboardData();
      fetchOpsAlerts();
      fetchAdminMetrics();
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isAuthorized]);

  // Format utilities
  const formatCurrency = (amount) => `$${amount.toLocaleString()}`;
  const formatDuration = (ms) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const mins = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${mins}m`;
  };

  if (isUnifiedAdminRoute && authRejected) {
    return <AdminBlank404 />;
  }

  if (
    isUnifiedAdminRoute &&
    !authClaims.pending &&
    !appSecret &&
    !isAdmin
  ) {
    return <AdminBlank404 />;
  }

  // Loading states - wait for admin JWT when there is no URL secret (admins may omit ?secret=)
  if (
    !isUnifiedAdminRoute &&
    !urlHadSecretOnMount &&
    user?.uid &&
    authClaims.pending
  ) {
    return (
      <div className="min-h-screen bg-surgical-navy-900 flex items-center justify-center">
        <div className="text-center">
          <div
            className="mx-auto mb-4 h-14 w-14 shrink-0 rounded-full border-[3px] border-surgical-navy-600 border-t-surgical-teal animate-spin"
            role="progressbar"
            aria-valuetext="Verifying access"
          />
          <div className="font-surgical text-surgical-teal text-lg font-bold uppercase tracking-wider">
            VERIFYING ACCESS
          </div>
          <div className="font-surgical text-gray-400 text-sm mt-2">
            Authenticating surgical credentials...
          </div>
        </div>
      </div>
    );
  }

  if (!isUiUnlocked && !isUnifiedAdminRoute) {
    return (
      <div className="min-h-screen bg-surgical-navy-900 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center space-y-6">
          <div
            className="mx-auto flex h-24 w-24 items-center justify-center rounded-2xl border-2 border-surgical-orange/60 bg-surgical-navy-800 text-5xl shadow-surgical-alert"
            aria-hidden
          >
            🔒
          </div>
          <h1 className="font-surgical text-2xl font-bold text-surgical-orange uppercase tracking-wider">
            Mission Control locked
          </h1>
          <p className="font-surgical text-sm text-gray-400 leading-relaxed">
            Open this page with your host{" "}
            <span className="text-surgical-teal font-bold">APP_SECRET</span> as{" "}
            <code className="text-surgical-orange">?auth_token=</code> on{" "}
            <code className="text-surgical-orange">/admin</code> (recommended) or
            legacy <code className="text-surgical-orange">?secret=</code> on{" "}
            <code className="text-surgical-orange">/ops</code>.
          </p>
          <div className="rounded-surgical border border-surgical-navy-600 bg-surgical-navy-800 px-4 py-3 font-surgical text-left text-xs text-surgical-teal break-all">
            {typeof window !== "undefined"
              ? `${window.location.origin}/admin?auth_token=…`
              : "/admin?auth_token=…"}
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthorized && !isUnifiedAdminRoute) {
    return (
      <div className="min-h-screen bg-surgical-navy-900 flex items-center justify-center">
        <div className="max-w-2xl mx-auto text-center space-y-8 p-8">
          <div className="space-y-4">
            <div className="w-20 h-20 border-4 border-surgical-orange rounded-full flex items-center justify-center mx-auto animate-pulse-surgical">
              <div className="w-8 h-8 bg-surgical-orange rounded-full"></div>
            </div>
            <h1 className="font-surgical text-3xl font-bold text-surgical-orange uppercase tracking-wider">
              MISSION CONTROL ACCESS
            </h1>
          </div>

          <div className="bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-6 space-y-4">
            <p className="font-surgical text-gray-300 text-sm">
              Secure access with your Netlify{" "}
              <span className="text-surgical-teal font-bold">APP_SECRET</span>{" "}
              credential:
            </p>
            <div className="bg-surgical-navy-900 border border-surgical-navy-700 rounded p-4 font-surgical text-surgical-teal text-sm">
              {typeof window !== "undefined"
                ? `${window.location.origin}/admin?auth_token=YOUR_APP_SECRET`
                : "/admin?auth_token=YOUR_APP_SECRET"}
            </div>
            <p className="font-surgical text-gray-400 text-xs">
              Or authenticate with Firebase admin credentials.
            </p>
            {user?.uid && !authClaims.pending && (
              <div className="bg-surgical-navy-700 border border-surgical-navy-600 rounded p-3 font-surgical text-xs text-gray-400">
                Authenticated as: {user.uid}
                <br />
                Admin status: {authClaims.admin ? "AUTHORIZED" : "UNAUTHORIZED"}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-surgical-navy-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-6 text-center">
          <div
            className="h-20 w-20 shrink-0 rounded-full border-4 border-surgical-navy-600 border-t-surgical-teal animate-spin"
            role="progressbar"
            aria-valuetext="Loading Mission Control"
          />
          <div className="space-y-2">
            <div className="font-surgical text-2xl font-bold text-surgical-teal uppercase tracking-wider">
              INITIALIZING MISSION CONTROL
            </div>
            <div className="font-surgical text-gray-400 text-sm">
              Establishing secure surgical connection...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-surgical-navy-900 flex items-center justify-center">
        <div className="max-w-lg mx-auto text-center space-y-6 p-8">
          <div className="w-20 h-20 border-4 border-red-500 rounded-full flex items-center justify-center mx-auto animate-pulse">
            <div className="text-red-500 text-2xl">⚠</div>
          </div>
          <div className="space-y-2">
            <div className="font-surgical text-2xl font-bold text-red-500 uppercase tracking-wider">
              CONNECTION ERROR
            </div>
            <div className="font-surgical text-gray-300 text-sm bg-surgical-navy-800 border border-red-500 rounded p-4">
              {error}
            </div>
          </div>
          <button
            onClick={fetchDashboardData}
            className="bg-surgical-teal hover:bg-surgical-teal-600 text-surgical-navy-900 font-surgical font-bold py-3 px-6 rounded transition-colors"
          >
            RECONNECT
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-surgical-navy-900 text-white font-sans">
      {/* Ops Alert Overlay */}
      <OpsAlertOverlay
        isVisible={showOpsAlert}
        alerts={opsAlerts}
        onClose={() => setShowOpsAlert(false)}
        onPauseDispatch={handlePauseDispatch}
      />

      {/* Header */}
      <header className="bg-surgical-navy-800 border-b border-surgical-navy-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <h1 className="font-surgical text-2xl font-bold text-surgical-teal uppercase tracking-wider">
              {isUnifiedAdminRoute ? "ADMIN" : "MISSION CONTROL"}
            </h1>
            <div
              className={`flex items-center space-x-2 font-surgical text-sm ${
                error ? "text-red-500" : "text-surgical-teal"
              }`}
            >
              <div
                className={`w-2 h-2 rounded-full ${
                  error
                    ? "bg-red-500 animate-pulse"
                    : "bg-surgical-teal animate-pulse-surgical"
                }`}
              ></div>
              <span className="uppercase tracking-wider font-bold">
                {error ? "DEGRADED" : dispatchPaused ? "PAUSED" : "OPERATIONAL"}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="font-surgical text-xs text-gray-400">
              LAST UPDATE: {lastRefresh?.toLocaleTimeString() || "NEVER"}
            </div>
            <button
              onClick={() => {
                fetchHealthStatus();
                fetchDashboardData();
              }}
              className="bg-surgical-navy-700 hover:bg-surgical-navy-600 border border-surgical-navy-500 text-surgical-teal font-surgical font-bold py-2 px-4 rounded transition-colors"
            >
              REFRESH
            </button>
          </div>
        </div>
      </header>

      <div className="p-6 space-y-6">
        {/* Preflight (left) + Revenue scorecard (top-right on wide viewports) */}
        <div className="flex flex-col xl:flex-row gap-6 items-stretch">
          <div className="min-w-0 flex-1">
            <div className="bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-6 shadow-surgical h-full">
              <h2 className="font-surgical text-lg font-bold text-surgical-teal uppercase tracking-wider mb-6">
                DEPLOYMENT READINESS - PREFLIGHT
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8">
                <StatusRing
                  status={
                    surgicalMetrics?.preflightStatus?.a2p10dlc?.status ||
                    "offline"
                  }
                  label="A2P 10DLC"
                  value={surgicalMetrics?.preflightStatus?.a2p10dlc?.value || 0}
                  size="lg"
                />
                <StatusRing
                  status={
                    surgicalMetrics?.preflightStatus?.domainWarmth?.status ||
                    "offline"
                  }
                  label="Domain Warmth"
                  value={
                    surgicalMetrics?.preflightStatus?.domainWarmth?.value || 0
                  }
                  size="lg"
                />
                <StatusRing
                  status={
                    surgicalMetrics?.preflightStatus?.inboxHealth?.status ||
                    "offline"
                  }
                  label="Inbox Health"
                  value={
                    surgicalMetrics?.preflightStatus?.inboxHealth?.value || 0
                  }
                  size="lg"
                />
                <StatusRing
                  status={
                    surgicalMetrics?.preflightStatus?.leadReadiness?.status ||
                    "offline"
                  }
                  label="Lead Readiness"
                  value={
                    surgicalMetrics?.preflightStatus?.leadReadiness?.value || 0
                  }
                  size="lg"
                />
              </div>
              <PreflightEnvInventory report={surgicalMetrics?.preflightEnv} />
            </div>
          </div>

          <div className="w-full xl:w-[22rem] shrink-0 xl:max-w-[24rem]">
            <div className="bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-6 shadow-surgical h-full xl:border-surgical-orange/40">
              <h2 className="font-surgical text-lg font-bold text-surgical-teal uppercase tracking-wider mb-4">
                REVENUE ENGINE
              </h2>
              {data.revenue?.error ? (
                <div className="font-surgical text-red-500 text-sm">
                  {data.revenue.error}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-baseline gap-3">
                    <span className="font-surgical text-sm text-gray-400 uppercase shrink-0">
                      24H
                    </span>
                    <span className="font-surgical text-4xl sm:text-5xl font-bold text-surgical-teal tabular-nums tracking-tight">
                      {formatCurrency(data.revenue.last24h)}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline gap-3">
                    <span className="font-surgical text-sm text-gray-400 uppercase shrink-0">
                      7D
                    </span>
                    <span className="font-surgical text-4xl sm:text-5xl font-bold text-surgical-teal tabular-nums tracking-tight">
                      {formatCurrency(data.revenue.last7d)}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline gap-3">
                    <span className="font-surgical text-sm text-gray-400 uppercase shrink-0">
                      30D
                    </span>
                    <span className="font-surgical text-4xl sm:text-5xl font-bold text-surgical-teal tabular-nums tracking-tight">
                      {formatCurrency(data.revenue.last30d)}
                    </span>
                  </div>
                  <div className="border-t border-surgical-navy-600 pt-4">
                    <div className="font-surgical text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                      <span>$25: {data.revenue.fees.collected25}</span>
                      <span>$95: {data.revenue.fees.collected95}</span>
                      <span>$295: {data.revenue.fees.collected295}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <AdminPerformancePanel metrics={adminMetrics} loading={adminMetricsLoading} />

        <div className="grid grid-cols-12 gap-6">
          {/* Enhanced Shark Tracker */}
          <div className="col-span-4 bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-6 shadow-surgical">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-surgical text-lg font-bold text-surgical-teal uppercase tracking-wider">
                SHARK TRACKER
              </h2>
              <StatusRing
                status={
                  (data.sharkStatus?.activeSearches ?? 0) > 10
                    ? "warning"
                    : (data.sharkStatus?.activeSearches ?? 0) > 0
                    ? "operational"
                    : "offline"
                }
                label=""
                value={Math.min(
                  100,
                  Math.max(0, (data.sharkStatus?.activeSearches ?? 0) * 8)
                )}
                centerLabel={String(data.sharkStatus?.activeSearches ?? 0)}
                size="sm"
              />
            </div>

            {data.sharkStatus?.error ? (
              <div className="space-y-3">
                <div className="font-surgical text-red-500 text-sm">
                  {data.sharkStatus.error}
                </div>
                <div className="bg-surgical-navy-700 border border-surgical-orange rounded p-3">
                  <div className="font-surgical text-xs font-bold text-surgical-orange mb-2">
                    FIRESTORE COMPOSITE INDEX REQUIRED
                  </div>
                  <div className="font-surgical text-xs text-gray-300 mb-2">
                    Create the following composite index for leads collection:
                  </div>
                  <div className="bg-surgical-navy-900 border border-surgical-navy-600 rounded p-2 font-surgical text-xs text-surgical-teal">
                    leads: (status ASC, zipCode ASC, createdAt DESC)
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-surgical text-sm text-gray-400 uppercase">
                    Active:
                  </span>
                  <span className="font-surgical text-xl font-bold text-surgical-teal">
                    {data.sharkStatus?.activeSearches || 0}
                  </span>
                </div>

                {/* Lead Velocity Sparkline */}
                <div>
                  <div className="font-surgical text-xs text-gray-400 uppercase mb-2">
                    Lead Velocity
                  </div>
                  <LeadVelocitySparkline data={leadVelocityData} />
                </div>

                {/* Active Jobs */}
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {data.sharkStatus?.searchingJobs
                    ?.filter((job) => job.bidCount < 5)
                    .map((job) => (
                      <div
                        key={job.id}
                        className="bg-surgical-navy-700 border border-surgical-navy-600 rounded p-2"
                      >
                        <div className="font-surgical text-xs text-white mb-1 truncate">
                          {job.description}
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="flex space-x-2">
                            <span className="bg-surgical-navy-600 px-2 py-1 rounded font-surgical text-xs text-gray-300">
                              {job.zipCode}
                            </span>
                            <span
                              className={`px-2 py-1 rounded font-surgical text-xs ${
                                job.hoursSinceDispatch > 4
                                  ? "bg-surgical-orange text-surgical-navy-900"
                                  : "bg-surgical-teal text-surgical-navy-900"
                              }`}
                            >
                              {job.hoursSinceDispatch}h
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              triggerManualIntervention(job.id, "shark_boost")
                            }
                            className="bg-surgical-orange hover:bg-surgical-orange-600 text-surgical-navy-900 font-surgical text-xs font-bold px-3 py-1 rounded transition-colors"
                          >
                            BOOST
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Hand-Raiser Feed */}
          <div className="col-span-4 bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-6 shadow-surgical">
            <h2 className="font-surgical text-lg font-bold text-surgical-teal uppercase tracking-wider mb-4">
              HAND-RAISER FEED
            </h2>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {(surgicalMetrics?.handRaiserFeed || []).map((item) => (
                <div
                  key={item.id}
                  className="bg-surgical-navy-700 border-l-4 border-surgical-teal rounded p-3"
                >
                  <div className="font-surgical text-xs text-white mb-2">
                    "{item.message}"
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="bg-surgical-teal text-surgical-navy-900 font-surgical text-xs font-bold px-2 py-1 rounded uppercase">
                      POSITIVE
                    </span>
                    <span className="font-surgical text-xs text-gray-400">
                      {(() => {
                        const tsMs =
                          typeof item.timestamp === "number"
                            ? item.timestamp
                            : new Date(item.timestamp).getTime();
                        const delta = Date.now() - tsMs;
                        if (!Number.isFinite(delta) || delta < 0) return " - ";
                        return `${formatDuration(delta)} ago`;
                      })()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Audit Latency */}
          <div className="col-span-4 bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-6 shadow-surgical">
            <h2 className="font-surgical text-lg font-bold text-surgical-teal uppercase tracking-wider mb-4">
              AUDIT LATENCY
            </h2>
            <div className="text-center space-y-4">
              <div className="space-y-2">
                <div className="font-surgical text-xs text-gray-400 uppercase">
                  Avg Lead → AI Spec
                </div>
                <div className="font-surgical text-4xl font-bold text-surgical-orange">
                  {surgicalMetrics?.auditLatency?.avgLatency || 2.3}
                  <span className="text-lg text-gray-400">s</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="font-surgical text-xs text-gray-400 uppercase">
                    Today
                  </div>
                  <div className="font-surgical text-lg font-bold text-surgical-teal">
                    {surgicalMetrics?.auditLatency?.todayAvg || 1.8}s
                  </div>
                </div>
                <div>
                  <div className="font-surgical text-xs text-gray-400 uppercase">
                    SLA
                  </div>
                  <div className="font-surgical text-lg font-bold text-gray-300">
                    {surgicalMetrics?.auditLatency?.slaTarget || 5.0}s
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* St. Louis Map View - dark Leaflet (Carto tiles) */}
          <div className="col-span-12 lg:col-span-8 bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-6 shadow-surgical">
            <h2 className="font-surgical text-lg font-bold text-surgical-teal uppercase tracking-wider mb-4">
              ST. LOUIS SHADOW AUDITS
            </h2>
            <Suspense
              fallback={
                <div className="h-64 rounded border border-surgical-navy-700 bg-surgical-navy-900 flex items-center justify-center font-surgical text-xs text-gray-500">
                  Loading map…
                </div>
              }
            >
              <ShadowAuditsMap
                kirkwood={surgicalMetrics?.stLouisAudits?.kirkwood ?? 12}
                southCity={surgicalMetrics?.stLouisAudits?.southCity ?? 8}
                stCharles={surgicalMetrics?.stLouisAudits?.stCharles ?? 15}
              />
            </Suspense>
            <div className="grid grid-cols-3 gap-3 mt-4 text-center font-surgical text-xs text-gray-400 uppercase">
              <div>
                <span className="text-surgical-teal font-bold text-base block normal-case">
                  {surgicalMetrics?.stLouisAudits?.kirkwood ?? 12}
                </span>
                Kirkwood
              </div>
              <div>
                <span className="text-surgical-teal font-bold text-base block normal-case">
                  {surgicalMetrics?.stLouisAudits?.southCity ?? 8}
                </span>
                South City
              </div>
              <div>
                <span className="text-surgical-teal font-bold text-base block normal-case">
                  {surgicalMetrics?.stLouisAudits?.stCharles ?? 15}
                </span>
                St. Charles
              </div>
            </div>
          </div>
        </div>

        {/* Mission Control Feed */}
        <div className="bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-6 shadow-surgical">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-surgical text-lg font-bold text-surgical-teal uppercase tracking-wider">
              MISSION CONTROL FEED
            </h2>
            <StatusRing
              status="operational"
              label="LIVE"
              value={100}
              centerLabel="●"
              size="sm"
            />
          </div>

          <div className="bg-surgical-navy-900 border border-surgical-navy-700 rounded overflow-hidden">
            <div className="grid grid-cols-6 bg-surgical-navy-700 border-b border-surgical-navy-600 p-4 font-surgical text-xs font-bold text-gray-400 uppercase tracking-wider">
              <div>USER</div>
              <div>LOCATION</div>
              <div>COMPLETION</div>
              <div>TIER</div>
              <div>ACTIVITY</div>
              <div>ACTIONS</div>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {activeChatSessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => fetchSessionDetails(session.id)}
                  className={`grid grid-cols-6 p-4 border-b border-surgical-navy-800 cursor-pointer transition-colors hover:bg-surgical-navy-800 ${
                    selectedSession?.id === session.id
                      ? "bg-surgical-navy-700"
                      : ""
                  }`}
                >
                  <div className="space-y-1">
                    <div className="font-surgical text-sm text-white">
                      {session.userId?.slice(-8) || "Unknown"}
                    </div>
                    <div
                      className={`font-surgical text-xs font-bold uppercase ${
                        session.lastActive > Date.now() - 300000
                          ? "text-surgical-teal"
                          : "text-gray-500"
                      }`}
                    >
                      {session.lastActive > Date.now() - 300000
                        ? "ACTIVE"
                        : "IDLE"}
                    </div>
                  </div>

                  <div className="font-surgical text-sm text-gray-300">
                    {session.zipCode || "N/A"}
                  </div>

                  <div className="flex items-center">
                    <div className="w-20 h-4 bg-surgical-navy-800 rounded overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-red-500 via-surgical-orange to-surgical-teal transition-all duration-300"
                        style={{ width: `${session.completion || 0}%` }}
                      ></div>
                    </div>
                    <span className="font-surgical text-xs text-white ml-2">
                      {session.completion || 0}%
                    </span>
                  </div>

                  <div
                    className={`font-surgical text-sm font-bold ${
                      session.feeTier === 295
                        ? "text-red-500"
                        : session.feeTier === 95
                        ? "text-surgical-orange"
                        : session.feeTier === 25
                        ? "text-surgical-teal"
                        : "text-gray-500"
                    }`}
                  >
                    ${session.feeTier || "TBD"}
                  </div>

                  <div className="font-surgical text-xs text-gray-400">
                    {session.lastActive
                      ? formatDuration(Date.now() - session.lastActive)
                      : "N/A"}
                  </div>

                  <div>
                    {session.jobId && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          triggerManualIntervention(
                            session.jobId,
                            "priority_escalation"
                          );
                        }}
                        className={`font-surgical text-xs font-bold px-3 py-1 rounded transition-colors ${
                          session.completion >= 100 && !session.dispatched
                            ? "bg-surgical-orange text-surgical-navy-900 animate-pulse-surgical"
                            : "bg-surgical-teal text-surgical-navy-900 hover:bg-surgical-teal-600"
                        }`}
                      >
                        ESCALATE
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Session Deep Dive */}
        {selectedSession && (
          <div className="bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-6 shadow-surgical">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-surgical text-lg font-bold text-surgical-teal uppercase tracking-wider">
                DEEP DIVE: {selectedSession.userId?.slice(-8)}
              </h2>
              <button
                onClick={() => setSelectedSession(null)}
                className="bg-surgical-navy-700 hover:bg-surgical-navy-600 border border-surgical-navy-500 text-gray-400 font-surgical text-sm font-bold py-2 px-4 rounded transition-colors"
              >
                CLOSE
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-surgical-navy-900 border border-surgical-navy-700 rounded p-4">
                <h3 className="font-surgical text-sm font-bold text-gray-400 uppercase mb-4">
                  USER ↔ CLUMSE AI TRANSCRIPT
                </h3>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {selectedSession.transcript?.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded border-l-4 ${
                        msg.role === "user"
                          ? "bg-surgical-navy-800 border-surgical-teal"
                          : "bg-surgical-navy-700 border-surgical-orange"
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-surgical text-xs font-bold text-gray-400 uppercase">
                          {msg.role}
                        </span>
                        <span className="font-surgical text-xs text-gray-500">
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="font-surgical text-xs text-white">
                        {msg.content}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-surgical-navy-900 border border-surgical-navy-700 rounded p-4">
                <h3 className="font-surgical text-sm font-bold text-gray-400 uppercase mb-4">
                  CONTRACTOR COMMUNICATIONS
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedSession.vendorLogs?.map((log, idx) => {
                    const isPortal = log.action?.includes("PORTAL");
                    return (
                      <div
                        key={idx}
                        className={`p-2 rounded border-l-2 ${
                          isPortal
                            ? "bg-surgical-navy-700 border-surgical-teal"
                            : "bg-surgical-navy-800 border-surgical-orange"
                        }`}
                      >
                        <div className="flex justify-between items-center mb-1">
                          <span
                            className={`font-surgical text-xs font-bold ${
                              isPortal
                                ? "text-surgical-teal"
                                : "text-surgical-orange"
                            }`}
                          >
                            {log.action}
                            {isPortal && (
                              <span className="bg-surgical-teal text-surgical-navy-900 font-surgical text-xs font-bold px-2 py-1 rounded ml-2 uppercase">
                                Portal
                              </span>
                            )}
                          </span>
                          <span className="font-surgical text-xs text-gray-500">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="font-surgical text-xs text-gray-300">
                          {log.details}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MissionControlSurgical;
